import { useEffect, useMemo, useState } from "react";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import { Textarea } from "../../ui/textarea";
import { RadioGroup, RadioGroupItem } from "../../ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "../../ui/card";
import { Badge } from "../../ui/badge";
import { Switch } from "../../ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../ui/dialog";
import { CheckCircle, Server, Loader2, Shield, Network, Key, Lock, Terminal, Globe, Settings, Plus, ChevronDown, ChevronUp, Trash2 } from "lucide-react";
import { addManualDevice, validateIpAddress } from "./Api/EndpointApi";
import { fetchCredentialByLabel, getCredentialsList, getGatewayCredentialsList } from "../Credentials/Api/credentialapi";
import { AddManualDevicePayload, ValidateGatewayResponse } from "../interfaces/EndpointInterfaces";
import { myLocalStorage } from "../../StorageHelper";

interface AddManualDeviceProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  updateEndpoints?: () => void;
  prefilledDevice?: {
    ipAddress?: string;
    hostname?: string;
    port?: number;
    protocol?: "SSH" | "RDP";
    gatewayName?: string;
  };
}

type Step = "validation" | "configuration";
type AuthType = "Key" | "Password";

interface DeviceCredential {
  id: string;
  usePrefetchedCredential: boolean;
  selectedPrefetchedCredentialId: string;
  authType: AuthType;
  username: string;
  privateKey: string;
  passphrase: string;
  password: string;
  confirmPassword: string;
}

interface PrefetchedCredentialOption {
  id: string;
  label: string;
  rawLabel?: string;
  username: string;
  protocol?: "SSH" | "RDP";
  protocolAuth?: AuthType;
  privateKey: string;
  passphrase: string;
  password: string;
  confirmPassword: string;
}

const createCredential = (authType: AuthType = "Key"): DeviceCredential => ({
  id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  usePrefetchedCredential: false,
  selectedPrefetchedCredentialId: "",
  authType,
  username: "",
  privateKey: "",
  passphrase: "",
  password: "",
  confirmPassword: "",
});

const normalizeProtocol = (value: unknown): "SSH" | "RDP" | undefined => {
  const normalized = `${value ?? ""}`.trim().toUpperCase();
  if (normalized === "SSH") return "SSH";
  if (normalized === "RDP") return "RDP";
  return undefined;
};

const normalizeAuthType = (value: unknown): AuthType | undefined => {
  const normalized = `${value ?? ""}`.trim().toUpperCase();
  if (normalized === "KEY") return "Key";
  if (normalized === "PASSWORD") return "Password";
  return undefined;
};

const hasCredentialLikeFields = (value: unknown) => {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return false;
  }

  const row = value as Record<string, unknown>;
  return Boolean(
    row.username ||
    row.userName ||
    row.credentialUsername ||
    row.password ||
    row.passWord ||
    row.privateKey ||
    row.private_key ||
    row.privatekey ||
    row.sshPrivateKey ||
    row.protocolAuth ||
    row.protocolType ||
    row.authType
  );
};

const unwrapCredentialObject = (response: unknown): Record<string, any> => {
  if (!response || typeof response !== "object" || Array.isArray(response)) {
    return {};
  }

  let current: Record<string, any> = response as Record<string, any>;
  for (let depth = 0; depth < 5; depth += 1) {
    if (hasCredentialLikeFields(current)) {
      return current;
    }

    const nestedCandidate = [
      current.data,
      current.content,
      current.credential,
      current.result,
      current.details,
      current.payload,
      current.value,
    ].find(
      (candidate) =>
        candidate && typeof candidate === "object" && !Array.isArray(candidate)
    ) as Record<string, any> | undefined;

    if (!nestedCandidate || nestedCandidate === current) {
      break;
    }

    current = nestedCandidate;
  }

  return current;
};

const extractCredentialRows = (response: unknown): Record<string, any>[] => {
  if (Array.isArray(response)) {
    return response as Record<string, any>[];
  }

  if (!response || typeof response !== "object") {
    return [];
  }

  const responseRecord = response as Record<string, unknown>;
  const candidates = [
    responseRecord.content,
    responseRecord.data,
    responseRecord.credentials,
    responseRecord.items,
    responseRecord.rows,
    responseRecord.result,
    responseRecord.results,
  ];

  const firstArray = candidates.find((candidate) => Array.isArray(candidate));
  return Array.isArray(firstArray) ? (firstArray as Record<string, any>[]) : [];
};

const toPrefetchedCredentialOption = (
  row: Record<string, any>,
  fallbackLabel = "",
  fallbackId = ""
): PrefetchedCredentialOption => {
  const rawLabelValue =
    row?.credentialLabel ??
    row?.label ??
    row?.name ??
    row?.credentialName ??
    row?.credential_label ??
    null;
  const rawLabel = rawLabelValue != null ? `${rawLabelValue}` : undefined;
  const label = (rawLabel ?? fallbackLabel).trim();

  const username = `${row?.credentialUsername ??
    row?.username ??
    row?.userName ??
    row?.credential_username ??
    row?.user_name ??
    ""
    }`.trim();

  const rawId =
    row?.id ??
    row?.credentialId ??
    row?.label ??
    row?.credentialLabel ??
    fallbackId ??
    label;

  return {
    id: `${rawId}`,
    label: label || username,
    rawLabel,
    username,
    protocol: normalizeProtocol(row?.protocol ?? row?.connectionProtocol),
    protocolAuth: normalizeAuthType(
      row?.protocolAuth ??
      row?.authType ??
      row?.protocol_auth ??
      row?.protocolType ??
      row?.protocol_type ??
      row?.protocolTypeAuth ??
      row?.protoTypeAuth ??
      row?.prototypeAuth ??
      row?.protoytypeAuth
    ),
    privateKey: `${row?.privateKey ??
      row?.sshPrivateKey ??
      row?.private_key ??
      row?.privatekey ??
      row?.private_Key ??
      row?.sshKey ??
      row?.ssh_key ??
      row?.credentialPrivateKey ??
      row?.key ??
      ""
      }`,
    passphrase: `${row?.passPharse ?? row?.passphrase ?? row?.passPhrase ?? ""
      }`,
    password: `${row?.password ??
      row?.passWord ??
      row?.credentialPassword ??
      row?.credential_password ??
      row?.secret ??
      row?.credentialSecret ??
      row?.value ??
      ""
      }`,
    confirmPassword: `${row?.confirmPassword ??
      row?.retypePassword ??
      row?.password ??
      row?.credentialPassword ??
      ""
      }`,
  };
};

const normalizePrefetchedCredentials = (response: unknown): PrefetchedCredentialOption[] => {
  const rows = extractCredentialRows(response);
  const unique = new Map<string, PrefetchedCredentialOption>();

  rows.forEach((row, index) => {
    const normalized = toPrefetchedCredentialOption(row, "", `${index}`);
    if (!normalized.label && !normalized.username) {
      return;
    }

    unique.set(normalized.id, normalized);
  });

  return Array.from(unique.values());
};

const normalizeCredentialByLabelResponse = (
  response: unknown,
  fallback: PrefetchedCredentialOption
): PrefetchedCredentialOption => {
  if (!response) {
    return fallback;
  }

  const source = unwrapCredentialObject(response);
  const normalized = toPrefetchedCredentialOption(source, fallback.label, fallback.id);

  const normalizedSecret = normalized.password || normalized.confirmPassword;
  const fallbackSecret = fallback.password || fallback.confirmPassword;

  return {
    ...fallback,
    ...normalized,
    label: normalized.label || fallback.label,
    username: normalized.username || fallback.username,
    protocol: normalized.protocol || fallback.protocol,
    protocolAuth: normalized.protocolAuth || fallback.protocolAuth,
    privateKey: normalized.privateKey || fallback.privateKey || "",
    passphrase: normalized.passphrase || fallback.passphrase,
    password: normalized.password || fallback.password || "",
    confirmPassword:
      normalized.confirmPassword ||
      normalizedSecret ||
      fallback.confirmPassword ||
      fallbackSecret,
  };
};

const getProtocolCompatibleCredentials = (
  credentials: PrefetchedCredentialOption[],
  selectedProtocol: string
) => {
  return credentials.filter(
    (credential) => !credential.protocol || credential.protocol === selectedProtocol
  );
};

export default function AddManualDevice({ open, onOpenChange, updateEndpoints = () => { }, prefilledDevice }: AddManualDeviceProps) {
  const [step, setStep] = useState<Step>("validation");
  const [isValidating, setIsValidating] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasAttemptedAutoValidation, setHasAttemptedAutoValidation] = useState(false);

  const [ipAddress, setIpAddress] = useState("");
  const [deviceName, setDeviceName] = useState("");

  const [protocol, setProtocol] = useState("SSH");
  const [portNumber, setPortNumber] = useState("22");
  const [credentials, setCredentials] = useState<DeviceCredential[]>([createCredential()]);
  const [gatewayComputerId, setGatewayComputerId] = useState(-1);

  const tenantName = myLocalStorage.getItem("latestTenant")?.tenantName || "";

  const [gatewayComputer, setGatewayComputer] = useState("");
  const [isValidated, setIsValidated] = useState(false);
  const [isGatewayIpValid, setIsGatewayIpValid] = useState("");
  const [ipError, setIpError] = useState("");
  const [isIPValid, setIsIPValid] = useState(false);
  const [expandedCredentials, setExpandedCredentials] = useState<Set<string>>(new Set([credentials[0]?.id]));
  const [prefetchedCredentials, setPrefetchedCredentials] = useState<PrefetchedCredentialOption[]>([]);
  const [isPrefetchingCredentials, setIsPrefetchingCredentials] = useState(false);
  const [isFetchingCredentialByLabel, setIsFetchingCredentialByLabel] = useState(false);
  const [prefetchedCredentialError, setPrefetchedCredentialError] = useState("");
  const [configurationError, setConfigurationError] = useState("");

  const isDiscoveryOnboardPrefill = useMemo(
    () =>
      Boolean(
        prefilledDevice?.ipAddress?.trim() &&
        prefilledDevice?.hostname?.trim() &&
        prefilledDevice?.protocol &&
        prefilledDevice?.port
      ),
    [prefilledDevice]
  );

  useEffect(() => {
    if (open) {
      resetForm(prefilledDevice);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, prefilledDevice]);

  useEffect(() => {
    if (!open || !isDiscoveryOnboardPrefill || hasAttemptedAutoValidation) {
      return;
    }

    if (step !== "validation" || isValidating || isValidated) {
      return;
    }

    if (!tenantName.trim() || !ipAddress.trim() || !deviceName.trim() || !isIPValid) {
      return;
    }

    setHasAttemptedAutoValidation(true);
    void handleValidate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    open,
    isDiscoveryOnboardPrefill,
    hasAttemptedAutoValidation,
    step,
    isValidating,
    isValidated,
    tenantName,
    ipAddress,
    deviceName,
    isIPValid,
  ]);

  const ipRegex =
    /^(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/;


  const validateIp = (value: string) => {
    const isValidIp = ipRegex.test(value);
    setIpError(isValidIp ? "" : "Invalid IP Address format");
    setIsIPValid(isValidIp);
  };

  const updateCredential = <K extends keyof DeviceCredential>(
    id: string,
    field: K,
    value: DeviceCredential[K]
  ) => {
    setConfigurationError("");
    setCredentials((prev) =>
      prev.map((credential) =>
        credential.id === id
          ? {
            ...credential,
            [field]: value,
          }
          : credential
      )
    );
  };

  const applyPrefetchedCredential = (
    credential: DeviceCredential,
    prefetched: PrefetchedCredentialOption,
    selectedProtocol: string
  ): DeviceCredential => {
    const hasPrivateKey = prefetched.privateKey.trim().length > 0;
    const hasPassword =
      prefetched.password.trim().length > 0 ||
      prefetched.confirmPassword.trim().length > 0;

    const fallbackAuthType: AuthType =
      selectedProtocol === "RDP"
        ? "Password"
        : credential.authType === "Password"
          ? "Password"
          : "Key";

    const resolvedAuthType: AuthType =
      selectedProtocol === "RDP"
        ? "Password"
        : prefetched.protocolAuth
          ? prefetched.protocolAuth
          : hasPrivateKey
            ? "Key"
            : hasPassword && fallbackAuthType === "Password"
              ? "Password"
              : fallbackAuthType;

    const requiresPassword =
      selectedProtocol === "RDP" || resolvedAuthType === "Password";

    const resolvedPrefetchedSecret =
      prefetched.password || prefetched.confirmPassword;

    const resolvedPrivateKey =
      prefetched.privateKey ||
      (!requiresPassword ? resolvedPrefetchedSecret : "") ||
      credential.privateKey;
    const resolvedPassword =
      requiresPassword ? resolvedPrefetchedSecret || credential.password : "";
    const resolvedConfirmPassword =
      requiresPassword
        ? prefetched.confirmPassword ||
        resolvedPrefetchedSecret ||
        credential.confirmPassword ||
        credential.password
        : "";

    return {
      ...credential,
      usePrefetchedCredential: true,
      selectedPrefetchedCredentialId: prefetched.id,
      authType: resolvedAuthType,
      username: prefetched.username || credential.username || prefetched.label,
      privateKey: requiresPassword ? "" : resolvedPrivateKey,
      passphrase: requiresPassword
        ? ""
        : prefetched.passphrase || credential.passphrase,
      password: resolvedPassword,
      confirmPassword: resolvedConfirmPassword,
    };
  };

  const prefetchCredentials = async (computerId: number | null) => {
    if (!tenantName.trim() || computerId == null || computerId < 0) {
      setPrefetchedCredentials([]);
      setPrefetchedCredentialError("");
      return;
    }

    setIsPrefetchingCredentials(true);
    setPrefetchedCredentialError("");
    try {
      const response = await getGatewayCredentialsList(tenantName, `${computerId}`);
      setPrefetchedCredentials(normalizePrefetchedCredentials(response));
    } catch (error: any) {
      const description =
        typeof error === "string"
          ? error
          : error?.message || "Unable to prefetch credentials for this gateway.";
      setPrefetchedCredentialError(description);
      setPrefetchedCredentials([]);
    } finally {
      setIsPrefetchingCredentials(false);
    }
  };

  const addNewCredential = () => {
    const authForProtocol: AuthType = protocol === "RDP" ? "Password" : "Key";
    const newCredential = createCredential(authForProtocol);
    setCredentials((prev) => [...prev, newCredential]);
    setExpandedCredentials((prev) => new Set([...prev, newCredential.id]));
  };

  const deleteCredential = (credentialId: string) => {
    if (credentials.length <= 1) {
      return;
    }

    setConfigurationError("");
    setCredentials((prev) => prev.filter((cred) => cred.id !== credentialId));
    setExpandedCredentials((prev) => {
      const next = new Set(prev);
      next.delete(credentialId);
      return next;
    });
  };

  const toggleCredentialExpand = (credentialId: string) => {
    setExpandedCredentials((prev) => {
      const next = new Set(prev);
      if (next.has(credentialId)) {
        next.delete(credentialId);
      } else {
        next.add(credentialId);
      }
      return next;
    });
  };

  const protocolCompatiblePrefetchedCredentials = useMemo(
    () => getProtocolCompatibleCredentials(prefetchedCredentials, protocol),
    [prefetchedCredentials, protocol]
  );

  const handlePrefetchedModeToggle = (credentialId: string, checked: boolean) => {
    const onlyProtocolCompatibleCredential =
      protocolCompatiblePrefetchedCredentials.length === 1
        ? protocolCompatiblePrefetchedCredentials[0]
        : null;

    setConfigurationError("");

    setCredentials((prev) =>
      prev.map((credential) => {
        if (credential.id !== credentialId) {
          return credential;
        }

        if (!checked) {
          return {
            ...credential,
            usePrefetchedCredential: false,
            selectedPrefetchedCredentialId: "",
            authType: protocol === "RDP" ? "Password" : "Key",
            username: "",
            privateKey: "",
            passphrase: "",
            password: "",
            confirmPassword: "",
          };
        }

        if (onlyProtocolCompatibleCredential) {
          return applyPrefetchedCredential(
            credential,
            onlyProtocolCompatibleCredential,
            protocol
          );
        }

        return {
          ...credential,
          usePrefetchedCredential: true,
          selectedPrefetchedCredentialId: "",
          authType: protocol === "RDP" ? "Password" : credential.authType,
          username: "",
          privateKey: "",
          passphrase: "",
          password: "",
          confirmPassword: "",
        };
      })
    );

    if (checked && onlyProtocolCompatibleCredential) {
      void handlePrefetchedCredentialSelect(
        credentialId,
        onlyProtocolCompatibleCredential.id
      );
    }
  };

  const handlePrefetchedCredentialSelect = async (
    credentialId: string,
    selectedPrefetchedCredentialId: string
  ) => {
    setConfigurationError("");

    const selectedSummary =
      protocolCompatiblePrefetchedCredentials.find(
        (credential) => credential.id === selectedPrefetchedCredentialId
      ) ||
      prefetchedCredentials.find(
        (credential) => credential.id === selectedPrefetchedCredentialId
      );

    setCredentials((prev) =>
      prev.map((credential) =>
        credential.id === credentialId
          ? selectedSummary
            ? applyPrefetchedCredential(
              {
                ...credential,
                selectedPrefetchedCredentialId,
              },
              {
                ...selectedSummary,
                id: selectedPrefetchedCredentialId,
              },
              protocol
            )
            : {
              ...credential,
              selectedPrefetchedCredentialId,
            }
          : credential
      )
    );

    if (!selectedSummary?.label) {
      return;
    }

    setIsFetchingCredentialByLabel(true);
    setPrefetchedCredentialError("");

    try {
      const response = await fetchCredentialByLabel(selectedSummary.label);
      const detailedCredential = normalizeCredentialByLabelResponse(
        response,
        selectedSummary
      );
      detailedCredential.id = selectedPrefetchedCredentialId;

      setCredentials((prev) =>
        prev.map((credential) => {
          if (credential.id !== credentialId) {
            return credential;
          }

          return applyPrefetchedCredential(
            {
              ...credential,
              selectedPrefetchedCredentialId,
            },
            detailedCredential,
            protocol
          );
        })
      );
    } catch (error: any) {
      const description =
        typeof error === "string"
          ? error
          : error?.message || "Unable to fetch credential details by label.";
      setPrefetchedCredentialError(description);

      // Fall back to list item values so user can still proceed.
      setCredentials((prev) =>
        prev.map((credential) => {
          if (credential.id !== credentialId) {
            return credential;
          }

          return applyPrefetchedCredential(
            {
              ...credential,
              selectedPrefetchedCredentialId,
            },
            {
              ...selectedSummary,
              id: selectedPrefetchedCredentialId,
            },
            protocol
          );
        })
      );
    } finally {
      setIsFetchingCredentialByLabel(false);
    }
  };

  useEffect(() => {
    if (
      protocolCompatiblePrefetchedCredentials.length !== 1 ||
      isFetchingCredentialByLabel
    ) {
      return;
    }

    const onlyCredential = protocolCompatiblePrefetchedCredentials[0];
    credentials.forEach((credential) => {
      if (credential.usePrefetchedCredential && !credential.selectedPrefetchedCredentialId) {
        void handlePrefetchedCredentialSelect(credential.id, onlyCredential.id);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [credentials, protocolCompatiblePrefetchedCredentials, isFetchingCredentialByLabel]);

  const duplicateUsernameCounts = useMemo(() => {
    const counts: Record<string, number> = {};

    credentials.forEach((credential) => {
      const normalizedUsername = credential.username.trim().toLowerCase();
      if (!normalizedUsername) {
        return;
      }

      counts[normalizedUsername] = (counts[normalizedUsername] || 0) + 1;
    });

    return counts;
  }, [credentials]);

  const isUsernameDuplicate = (username: string) => {
    const normalizedUsername = username.trim().toLowerCase();
    return normalizedUsername.length > 0 && (duplicateUsernameCounts[normalizedUsername] || 0) > 1;
  };

  const isCredentialValid = (credential: DeviceCredential) => {
    if (!credential.username.trim()) return false;
    if (isUsernameDuplicate(credential.username)) return false;

    if (credential.usePrefetchedCredential) {
      if (!credential.selectedPrefetchedCredentialId.trim()) {
        return false;
      }

      if (protocol === "SSH" && credential.authType === "Key") {
        return credential.privateKey.trim().length > 0;
      }

      return true;
    }

    if (protocol === "SSH" && credential.authType === "Key") {
      return credential.privateKey.trim().length > 0;
    }

    return (
      credential.password.trim().length > 0 &&
      credential.confirmPassword.trim().length > 0 &&
      credential.password === credential.confirmPassword
    );
  };

  const hasInvalidCredentials =
    credentials.length === 0 ||
    credentials.some((credential) => !isCredentialValid(credential));

  const handleValidate = async () => {
    if (!ipAddress.trim() || !deviceName.trim() || !tenantName) {
      return;
    }
    setIsValidating(true);
    try {
      let response: ValidateGatewayResponse = await validateIpAddress(
        tenantName,
        ipAddress,
        deviceName
      );

      if (response.isValid) {
        const nextGatewayComputerId = response.gatewayComputerId ?? -1;
        setGatewayComputerId(nextGatewayComputerId);
        setIsValidated(true);
        setIsGatewayIpValid("");
        setStep("configuration");
        setGatewayComputer(response.gatewayName || "");
        await prefetchCredentials(nextGatewayComputerId);
      } else {
        setIsValidated(false);
        setIsGatewayIpValid(response.error || "Error Validating IP Address");
        setPrefetchedCredentials([]);
        setPrefetchedCredentialError("");
      }
    } finally {
      setIsValidating(false);
    }
  };

  const handleSubmit = async () => {
    setConfigurationError("");
    setIsSubmitting(true);

    const normalizedCredentials = credentials.map((credential) => {
      const privateKey = credential.privateKey;
      const privateString = privateKey.includes("\\n")
        ? privateKey.replace(/\\n/g, "\n")
        : privateKey;

      return {
        username: credential.username,
        protocolAuth: protocol === "RDP" ? "Password" : credential.authType,
        privateKey: privateString,
        passPharse: credential.passphrase,
        password:
          protocol === "RDP" || credential.authType === "Password"
            ? credential.password
            : "",
        confirmPassword:
          protocol === "RDP" || credential.authType === "Password"
            ? credential.confirmPassword
            : "",
      };
    });

    const primaryCredential = normalizedCredentials[0];

    const data: AddManualDevicePayload = {
      ipAddress: ipAddress,
      gatewayComputerId: gatewayComputerId,
      tenantName: tenantName,
      deviceName: deviceName,
      protocol: protocol,
      portNumber: portNumber,
      username: primaryCredential?.username || "",
      password: primaryCredential?.password || "",
      confirmPassword: primaryCredential?.confirmPassword || "",
      protocolAuth: primaryCredential?.protocolAuth || "",
      privateKey: primaryCredential?.privateKey || "",
      passPharse: primaryCredential?.passPharse || "",
      credentials: normalizedCredentials,
    };

    try {
      await addManualDevice(data);
      updateEndpoints();

      // Simulate device addition
      await new Promise(resolve => setTimeout(resolve, 2000));

      console.log('Enterprise Action: Manual device added', {
        ipAddress,
        deviceName,
        protocol,
        portNumber,
        credentials: normalizedCredentials.length,
        gatewayComputer,
        timestamp: new Date().toISOString(),
        action: 'ADD_MANUAL_DEVICE'
      });

      onOpenChange(false);
      resetForm();
    } catch (error: any) {
      const description =
        typeof error === "string"
          ? error
          : error?.message || "Unable to deploy configuration.";
      setConfigurationError(description);
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = (initialPrefilledDevice?: AddManualDeviceProps["prefilledDevice"]) => {
    const defaultIpAddress = initialPrefilledDevice?.ipAddress?.trim() || "";
    const defaultDeviceName = initialPrefilledDevice?.hostname?.trim() || "";
    const defaultProtocol = initialPrefilledDevice?.protocol === "RDP" ? "RDP" : "SSH";
    const defaultPortNumber =
      typeof initialPrefilledDevice?.port === "number" && initialPrefilledDevice.port > 0
        ? `${initialPrefilledDevice.port}`
        : defaultProtocol === "RDP"
          ? "3389"
          : "22";
    const isDefaultIpValid = defaultIpAddress ? ipRegex.test(defaultIpAddress) : false;

    setStep("validation");
    setIpAddress(defaultIpAddress);
    setDeviceName(defaultDeviceName);
    setProtocol(defaultProtocol);
    setPortNumber(defaultPortNumber);
    setGatewayComputerId(-1);
    const newCreds = [createCredential(defaultProtocol === "RDP" ? "Password" : "Key")];
    setCredentials(newCreds);
    setExpandedCredentials(new Set([newCreds[0]?.id]));
    setPrefetchedCredentials([]);
    setPrefetchedCredentialError("");
    setConfigurationError("");
    setIsPrefetchingCredentials(false);
    setIsFetchingCredentialByLabel(false);
    setGatewayComputer(initialPrefilledDevice?.gatewayName || "");
    setIsValidated(false);
    setIsGatewayIpValid("");
    setIpError(defaultIpAddress && !isDefaultIpValid ? "Invalid IP Address format" : "");
    setIsIPValid(isDefaultIpValid);
    setIsValidating(false);
    setIsSubmitting(false);
    setHasAttemptedAutoValidation(false);
  };

  const handleCancel = () => {
    onOpenChange(false);
    resetForm();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[480px] max-h-[90vh] overflow-y-auto bg-gradient-to-br from-slate-50 via-white to-slate-100/30 dark:from-slate-900 dark:via-slate-800 dark:to-slate-900/20 border border-slate-200 dark:border-slate-700">
        <DialogHeader className="pb-4 border-b border-blue-100 dark:border-blue-800/50">
          <DialogTitle className="text-xl font-bold flex items-center gap-2 text-slate-800 dark:text-slate-100">
            <div className="p-1.5 rounded-lg bg-gradient-to-br from-blue-500 to-blue-600 text-white">
              <Server className="w-5 h-5" />
            </div>
            Add Device
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-5 pt-4">
          {step === "validation" && (
            <>
              <Card className="border-blue-200 dark:border-blue-800/50 shadow-lg bg-white/80 dark:bg-slate-800/80 backdrop-blur-sm">
                <CardHeader className="pb-4">
                  <CardTitle className="text-lg flex items-center gap-2 text-slate-700 dark:text-slate-200">
                    <Network className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                    Network Configuration
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* IP Address Field */}
                  <div className="space-y-2">
                    <Label htmlFor="ip-address" className="text-sm font-semibold text-slate-600 dark:text-slate-300 flex items-center gap-2">
                      <Globe className="w-4 h-4" />
                      IP Address
                    </Label>
                    <Input
                      id="ip-address"
                      placeholder="10.166.0.3"
                      value={ipAddress}
                      onChange={(e) => {
                        validateIp(e.target.value);
                        setIpAddress(e.target.value)
                        setIsGatewayIpValid("")
                      }}
                      readOnly={isDiscoveryOnboardPrefill}
                      className={`h-11 text-base border-2 border-slate-200 dark:border-slate-700 focus:border-blue-500 dark:focus:border-blue-400 rounded-lg shadow-sm transition-all duration-200 ${isDiscoveryOnboardPrefill ? "bg-slate-100 dark:bg-slate-800 cursor-not-allowed" : "bg-white dark:bg-slate-900"}`}
                      data-testid="input-ip-address"
                    />
                  </div>

                  {/* Device Name Field */}
                  <div className="space-y-2">
                    <Label htmlFor="device-name" className="text-sm font-semibold text-blue-600 dark:text-blue-400 flex items-center gap-2">
                      <Server className="w-4 h-4" />
                      Device Name
                    </Label>
                    <Input
                      id="device-name"
                      placeholder="test-vm"
                      value={deviceName}
                      onChange={(e) => setDeviceName(e.target.value)}
                      className="h-11 text-base border-2 border-blue-200 dark:border-blue-800 focus:border-blue-500 dark:focus:border-blue-400 rounded-lg shadow-sm transition-all duration-200 bg-white dark:bg-slate-900"
                      data-testid="input-device-name"
                    />
                  </div>


                  {
                    isGatewayIpValid.length > 0 &&


                    <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                      <p className="text-sm text-red-600 dark:text-red-400 font-medium">{isGatewayIpValid}</p>
                    </div>

                  }


                  <Button
                    onClick={handleValidate}
                    disabled={!ipAddress.trim() || !deviceName.trim() || isValidating || !isIPValid}
                    className="w-full h-12 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 dark:from-blue-600 dark:to-blue-700 text-white font-semibold rounded-lg shadow-lg hover:shadow-xl transition-all duration-200"
                    data-testid="button-validate-device"
                  >
                    {isValidating ? (
                      <>
                        <Loader2 className="w-5 h-5 mr-3 animate-spin" />
                        <span className="text-base">Validating Network...</span>
                      </>
                    ) : (
                      <>
                        <Shield className="w-5 h-5 mr-3" />
                        <span className="text-base">Validate Configuration</span>
                      </>
                    )}
                  </Button>
                </CardContent>
              </Card>

              {/* Action Buttons */}
              <div className="flex justify-end gap-3 pt-4 border-t border-blue-100 dark:border-blue-800/50">
                <Button
                  variant="outline"
                  onClick={handleCancel}
                  className="px-6 py-3 rounded-xl border-2 hover:bg-slate-50 dark:hover:bg-slate-800"
                  data-testid="button-cancel"
                >
                  Cancel
                </Button>
                <Button
                  disabled
                  className="px-6 py-3 bg-slate-300 dark:bg-slate-700 text-slate-500 dark:text-slate-400 rounded-xl cursor-not-allowed"
                  data-testid="button-submit-disabled"
                >
                  Submit
                </Button>
              </div>
            </>
          )}

          {step === "configuration" && (
            <>
              {/* Validation Success Summary */}
              <Card className="border-green-200 dark:border-green-800/50 bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 shadow-lg">
                <CardContent className="pt-4">
                  <div className="space-y-3">
                    {/* Validated IP Address */}
                    <div className="space-y-2">
                      <Label htmlFor="validated-ip" className="text-sm font-semibold text-slate-600 dark:text-slate-300 flex items-center gap-2">
                        <Globe className="w-4 h-4" />
                        IP Address
                      </Label>
                      <div className="relative">
                        <Input
                          id="validated-ip"
                          value={ipAddress}
                          readOnly
                          className="h-12 text-base pr-12 bg-white dark:bg-slate-900 border-green-200 dark:border-green-800"
                          data-testid="input-validated-ip"
                        />
                        <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-1">
                          <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400" />
                          <Badge variant="secondary" className="text-xs bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300">Verified</Badge>
                        </div>
                      </div>
                    </div>

                    {/* Gateway Computer Info */}
                    <div className="p-3 bg-blue-50 dark:bg-blue-900/30 rounded-lg border border-blue-200 dark:border-blue-800">
                      <div className="text-sm font-medium text-blue-700 dark:text-blue-300 flex items-center gap-2">
                        <Settings className="w-4 h-4" />
                        Gateway Computer: <span className="font-mono">{gatewayComputer}</span>
                      </div>
                    </div>

                    {/* Validated Device Name */}
                    <div className="space-y-2">
                      <Label htmlFor="validated-name" className="text-sm font-semibold text-slate-600 dark:text-slate-300 flex items-center gap-2">
                        <Server className="w-4 h-4" />
                        Device Name
                      </Label>
                      <div className="relative">
                        <Input
                          id="validated-name"
                          value={deviceName}
                          onChange={(e) => setDeviceName(e.target.value)}
                          className="h-12 text-base pr-12 bg-white dark:bg-slate-900 border-green-200 dark:border-green-800"
                          data-testid="input-validated-name"
                        />
                        <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-1">
                          <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400" />
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Network Configuration */}
              <Card className="border-blue-200 dark:border-blue-800/50 shadow-lg bg-white/80 dark:bg-slate-800/80 backdrop-blur-sm">
                <CardHeader className="pb-4">
                  <CardTitle className="text-lg flex items-center gap-2 text-slate-700 dark:text-slate-200">
                    <Terminal className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                    Connection Settings
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Protocol Dropdown */}
                  <div className="space-y-2">
                    <Label htmlFor="protocol" className="text-sm font-semibold text-slate-600 dark:text-slate-300">
                      Protocol
                    </Label>
                    <Select value={protocol} onValueChange={(value) => {
                      setConfigurationError("");
                      setProtocol(value);
                      const nextProtocol = value === "RDP" ? "RDP" : "SSH";

                      // Auto-set port based on protocol
                      if (nextProtocol === "SSH") {
                        setPortNumber("22");
                      } else {
                        setPortNumber("3389");
                      }

                      setCredentials((prev) =>
                        prev.map((credential) => {
                          if (!credential.usePrefetchedCredential) {
                            return {
                              ...credential,
                              authType: nextProtocol === "RDP" ? "Password" : "Key",
                            };
                          }

                          const selectedPrefetchedCredential = prefetchedCredentials.find(
                            (prefetched) =>
                              prefetched.id === credential.selectedPrefetchedCredentialId
                          );

                          if (!selectedPrefetchedCredential) {
                            return {
                              ...credential,
                              authType: nextProtocol === "RDP" ? "Password" : "Key",
                            };
                          }

                          const isProtocolCompatible =
                            !selectedPrefetchedCredential.protocol ||
                            selectedPrefetchedCredential.protocol === nextProtocol;

                          if (!isProtocolCompatible) {
                            return {
                              ...credential,
                              usePrefetchedCredential: false,
                              selectedPrefetchedCredentialId: "",
                              authType: nextProtocol === "RDP" ? "Password" : "Key",
                              username: "",
                              privateKey: "",
                              passphrase: "",
                              password: "",
                              confirmPassword: "",
                            };
                          }

                          return applyPrefetchedCredential(
                            credential,
                            selectedPrefetchedCredential,
                            nextProtocol
                          );
                        })
                      );
                    }} disabled={isDiscoveryOnboardPrefill}>
                      <SelectTrigger className="h-11 border-2 rounded-lg" data-testid="select-protocol">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="SSH">SSH</SelectItem>
                        <SelectItem value="RDP">RDP</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Port Number */}
                  <div className="space-y-2">
                    <Label htmlFor="port-number" className="text-sm font-semibold text-slate-600 dark:text-slate-300">
                      Port Number
                    </Label>
                    <Input
                      id="port-number"
                      value={portNumber}
                      onChange={(e) => setPortNumber(e.target.value)}
                      readOnly={isDiscoveryOnboardPrefill}
                      className="h-11 text-base border-2 rounded-lg"
                      data-testid="input-port-number"
                    />
                  </div>
                </CardContent>
              </Card>

              {/* Authentication Configuration */}
              <Card className="border-slate-200 dark:border-slate-700 shadow-lg bg-white/80 dark:bg-slate-800/80 backdrop-blur-sm">
                <CardHeader className="pb-4">
                  <CardTitle className="text-lg flex items-center gap-2 text-slate-700 dark:text-slate-200">
                    <Shield className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                    Security Authentication
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Authentication Type */}
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <Label className="text-sm font-semibold text-slate-600 dark:text-slate-300">
                        Credentials
                      </Label>
                      <Button
                        type="button"
                        variant="outline"
                        onClick={addNewCredential}
                        className="h-9 px-3"
                        data-testid="button-add-new-credential"
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        Add Credential
                      </Button>
                    </div>

                    <div className="text-xs text-slate-500 dark:text-slate-400">
                      {isPrefetchingCredentials
                        ? "Loading saved credentials from credential API..."
                        : `${protocolCompatiblePrefetchedCredentials.length} saved credential label(s) available for ${protocol}.`}
                    </div>

                    {credentials.map((credential, index) => (
                      <div key={credential.id} className="border-2 border-slate-200 dark:border-slate-700 rounded-xl bg-slate-50/50 dark:bg-slate-900/30 overflow-hidden">
                        {/* Expandable Header */}
                        <div
                          className="flex items-center justify-between p-4 cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-900/50 transition-colors"
                          onClick={() => toggleCredentialExpand(credential.id)}
                        >
                          <div className="flex items-center justify-between flex-1">
                            <Label className="text-sm font-semibold text-slate-700 dark:text-slate-200 cursor-pointer">
                              Credential {index + 1}
                            </Label>
                            <Badge variant="secondary" className="text-xs">
                              {protocol === "RDP" ? "Password" : credential.authType}
                            </Badge>
                          </div>
                          <div className="flex items-center gap-2">
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                deleteCredential(credential.id);
                              }}
                              disabled={credentials.length <= 1}
                              className="h-8 w-8 p-0 text-red-600 hover:bg-red-50 dark:hover:bg-red-950 disabled:opacity-50 disabled:cursor-not-allowed"
                              title={credentials.length <= 1 ? "At least one credential must be retained" : "Delete credential"}
                              data-testid={`button-delete-credential-${index}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                            {expandedCredentials.has(credential.id) ? (
                              <ChevronUp className="w-5 h-5 text-slate-600 dark:text-slate-400" />
                            ) : (
                              <ChevronDown className="w-5 h-5 text-slate-600 dark:text-slate-400" />
                            )}
                          </div>
                        </div>

                        {/* Expandable Content */}
                        {expandedCredentials.has(credential.id) && (
                          <div className="space-y-4 p-4 border-t border-slate-200 dark:border-slate-700">

                            <div className="space-y-3 rounded-lg border border-dashed border-slate-300 dark:border-slate-600 bg-slate-100/60 dark:bg-slate-900/40 p-3">
                              <div className="flex items-start justify-between gap-3">
                                <div>
                                  <Label className="text-sm font-semibold text-slate-700 dark:text-slate-200">
                                    Use Saved Credential
                                  </Label>
                                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                                    Enable to select a credential label and auto-fill security details.
                                  </p>
                                </div>
                                <Switch
                                  checked={credential.usePrefetchedCredential}
                                  onCheckedChange={(checked) =>
                                    handlePrefetchedModeToggle(credential.id, checked)
                                  }
                                  disabled={
                                    isPrefetchingCredentials ||
                                    (protocolCompatiblePrefetchedCredentials.length === 0 &&
                                      !credential.usePrefetchedCredential)
                                  }
                                  data-testid={`switch-prefetched-credential-${index}`}
                                />
                              </div>

                              {credential.usePrefetchedCredential && (
                                <div className="space-y-2">
                                  <Label
                                    htmlFor={`select-prefetched-label-${credential.id}`}
                                    className="text-sm font-semibold text-slate-600 dark:text-slate-300"
                                  >
                                    Credential Label
                                  </Label>
                                  <Select
                                    value={credential.selectedPrefetchedCredentialId}
                                    disabled={isFetchingCredentialByLabel}
                                    onValueChange={(value) =>
                                      void handlePrefetchedCredentialSelect(credential.id, value)
                                    }
                                  >
                                    <SelectTrigger
                                      id={`select-prefetched-label-${credential.id}`}
                                      className="h-11 border-2 rounded-lg"
                                      data-testid={`select-prefetched-credential-${index}`}
                                    >
                                      <SelectValue placeholder="Select saved credential label" />
                                    </SelectTrigger>
                                    <SelectContent className="max-h-64 overflow-y-auto">
                                      {protocolCompatiblePrefetchedCredentials.map((prefetched) => (
                                        <SelectItem key={prefetched.id} value={prefetched.id}>
                                          {prefetched.rawLabel || prefetched.label}
                                          {prefetched.username
                                            ? ` (${prefetched.username})`
                                            : ""}
                                        </SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                </div>
                              )}

                              {!isPrefetchingCredentials &&
                                protocolCompatiblePrefetchedCredentials.length === 0 && (
                                  <p className="text-xs text-amber-700 dark:text-amber-400">
                                    No saved credentials found for the selected protocol.
                                  </p>
                                )}

                              {isFetchingCredentialByLabel && (
                                <p className="text-xs text-blue-600 dark:text-blue-400">
                                  Fetching selected credential details...
                                </p>
                              )}

                              {prefetchedCredentialError && !isPrefetchingCredentials && (
                                <p className="text-xs text-red-600 dark:text-red-400">
                                  {prefetchedCredentialError}
                                </p>
                              )}
                            </div>

                            {protocol === "RDP" ? (
                              <div className="p-3 border border-blue-200 dark:border-blue-700 rounded-lg bg-blue-50/50 dark:bg-blue-900/20">
                                <div className="flex items-center gap-2">
                                  <Lock className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                                  <Label className="font-semibold text-blue-600 dark:text-blue-400">Password Authentication</Label>
                                </div>
                                <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">RDP protocol requires password authentication</p>
                              </div>
                            ) : (
                              <RadioGroup
                                value={credential.authType}
                                onValueChange={(value: AuthType) => updateCredential(credential.id, "authType", value)}
                                disabled={credential.usePrefetchedCredential}
                                className="grid grid-cols-2 gap-4"
                                data-testid={`radio-auth-type-${index}`}
                              >
                                <div className="flex items-center space-x-3 p-4 border-2 border-slate-200 dark:border-slate-700 rounded-xl hover:border-blue-300 dark:hover:border-blue-600 transition-colors">
                                  <RadioGroupItem
                                    value="Key"
                                    id={`key-${credential.id}`}
                                    className="text-blue-600"
                                    disabled={credential.usePrefetchedCredential}
                                  />
                                  <div className="flex items-center gap-2">
                                    <Key className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                                    <Label htmlFor={`key-${credential.id}`} className="font-semibold cursor-pointer">SSH Key</Label>
                                  </div>
                                </div>
                                <div className="flex items-center space-x-3 p-4 border-2 border-slate-200 dark:border-slate-700 rounded-xl hover:border-blue-300 dark:hover:border-blue-600 transition-colors">
                                  <RadioGroupItem
                                    value="Password"
                                    id={`password-${credential.id}`}
                                    className="text-blue-600"
                                    disabled={credential.usePrefetchedCredential}
                                  />
                                  <div className="flex items-center gap-2">
                                    <Lock className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                                    <Label htmlFor={`password-${credential.id}`} className="font-semibold cursor-pointer">Password</Label>
                                  </div>
                                </div>
                              </RadioGroup>
                            )}

                            <div className="space-y-2">
                              <Label htmlFor={`username-${credential.id}`} className="text-sm font-semibold text-slate-600 dark:text-slate-300">
                                Username
                              </Label>
                              <Input
                                id={`username-${credential.id}`}
                                value={credential.username}
                                onChange={(e) => updateCredential(credential.id, "username", e.target.value)}
                                disabled={credential.usePrefetchedCredential}
                                className="h-11 text-base border-2 rounded-lg"
                                data-testid={`input-username-${index}`}
                              />
                              {credential.username.trim().length > 0 && isUsernameDuplicate(credential.username) && (
                                <p className="text-sm text-red-600 dark:text-red-400 flex items-center gap-1">
                                  <span className="w-1 h-1 bg-red-600 rounded-full"></span>
                                  Username must be unique
                                </p>
                              )}
                            </div>

                            {protocol === "SSH" && credential.authType === "Key" ? (
                              <>
                                <div className="space-y-2">
                                  <Label htmlFor={`private-key-${credential.id}`} className="text-sm font-semibold text-slate-600 dark:text-slate-300 flex items-center gap-2">
                                    <Key className="w-4 h-4" />
                                    Private Key
                                  </Label>
                                  <Textarea
                                    id={`private-key-${credential.id}`}
                                    value={credential.privateKey}
                                    onChange={(e) => updateCredential(credential.id, "privateKey", e.target.value)}
                                    disabled={credential.usePrefetchedCredential}
                                    className="min-h-[120px] text-sm font-mono border-2 rounded-lg resize-none"
                                    placeholder="-----BEGIN PRIVATE KEY-----&#10;[Your private key content here]&#10;-----END PRIVATE KEY-----"
                                    data-testid={`textarea-private-key-${index}`}
                                  />
                                </div>

                                <div className="space-y-2">
                                  <Label htmlFor={`passphrase-${credential.id}`} className="text-sm font-semibold text-slate-600 dark:text-slate-300 flex items-center gap-2">
                                    <Lock className="w-4 h-4" />
                                    Passphrase <Badge variant="secondary" className="text-xs">Optional</Badge>
                                  </Label>
                                  <Input
                                    id={`passphrase-${credential.id}`}
                                    type="password"
                                    value={credential.passphrase}
                                    onChange={(e) => updateCredential(credential.id, "passphrase", e.target.value)}
                                    disabled={credential.usePrefetchedCredential}
                                    className="h-11 text-base border-2 rounded-lg"
                                    data-testid={`input-passphrase-${index}`}
                                  />
                                </div>
                              </>
                            ) : (
                              <>
                                <div className="space-y-2">
                                  <Label htmlFor={`auth-password-${credential.id}`} className="text-sm font-semibold text-slate-600 dark:text-slate-300 flex items-center gap-2">
                                    <Lock className="w-4 h-4" />
                                    Password
                                  </Label>
                                  <Input
                                    id={`auth-password-${credential.id}`}
                                    type="password"
                                    value={credential.password}
                                    onChange={(e) => updateCredential(credential.id, "password", e.target.value)}
                                    disabled={credential.usePrefetchedCredential}
                                    className="h-11 text-base border-2 rounded-lg"
                                    data-testid={`input-password-${index}`}
                                  />
                                </div>

                                <div className="space-y-2">
                                  <Label htmlFor={`confirm-password-${credential.id}`} className="text-sm font-semibold text-slate-600 dark:text-slate-300 flex items-center gap-2">
                                    <Lock className="w-4 h-4" />
                                    Confirm Password
                                  </Label>
                                  <Input
                                    id={`confirm-password-${credential.id}`}
                                    type="password"
                                    value={credential.confirmPassword}
                                    onChange={(e) => updateCredential(credential.id, "confirmPassword", e.target.value)}
                                    disabled={credential.usePrefetchedCredential}
                                    className="h-11 text-base border-2 rounded-lg"
                                    data-testid={`input-confirm-password-${index}`}
                                  />
                                  {credential.password && credential.confirmPassword && credential.password !== credential.confirmPassword && (
                                    <p className="text-sm text-red-600 dark:text-red-400 flex items-center gap-1">
                                      <span className="w-1 h-1 bg-red-600 rounded-full"></span>
                                      Passwords do not match
                                    </p>
                                  )}
                                </div>
                              </>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Action Buttons */}
              <div className="flex justify-end gap-3 pt-4 border-t border-slate-200 dark:border-slate-700">
                {configurationError && (
                  <p className="mr-auto self-center text-sm text-red-600 dark:text-red-400">
                    {configurationError}
                  </p>
                )}
                <Button
                  variant="outline"
                  onClick={handleCancel}
                  disabled={isSubmitting}
                  className="px-8 py-3 rounded-xl border-2 hover:bg-slate-50 dark:hover:bg-slate-800 transition-all duration-200"
                  data-testid="button-cancel-config"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleSubmit}
                  disabled={
                    isSubmitting ||
                    isFetchingCredentialByLabel ||
                    hasInvalidCredentials
                  }
                  className="px-8 py-3 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all duration-200 transform hover:scale-[1.02]"
                  data-testid="button-submit-device"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-3 animate-spin" />
                      <span className="text-base">Configuring Device...</span>
                    </>
                  ) : (
                    <>
                      <Shield className="w-5 h-5 mr-3" />
                      <span className="text-base">Deploy Configuration</span>
                    </>
                  )}
                </Button>
              </div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}